import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const brandStyles = {
    'armolan': { color: '#1E3A8A', logo: '🛡️' },
    'madico': { color: '#059669', logo: '🌿' },
    'solar-gard': { color: '#F59E0B', logo: '☀️' },
    'bruxsafol': { color: '#7C3AED', logo: '🔮' },
    'llumar': { color: '#DC2626', logo: '🔥' }
  };

  useEffect(() => {
    const fetchBrand = async () => {
      try {
        const response = await axios.get(`${API}/brands/${slug}`);
        setBrand(response.data);
        // Fetch products for this brand
        try {
          const prodResponse = await axios.get(`${API}/products?brand_id=${response.data.id}`);
          setProducts(prodResponse.data.products || []);
        } catch (e) {
          // Products fetch is optional
        }
      } catch (err) {
        console.error('Error fetching brand:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchBrand();
  }, [slug]);

  const getTranslation = (obj, field) => {
    const lang = getUiLang(i18n);
    return obj?.translations?.[lang]?.[field] || obj?.translations?.['sk']?.[field] || obj?.translations?.['en']?.[field] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">{t('brands.not_found', 'Brand not found')}</h1>
        <Link to="/brands" className="text-orange-500 hover:underline">
          {t('brands.back', '← Back to brands')}
        </Link>
      </div>
    );
  }

  const brandName = getTranslation(brand, 'name') || slug;
  const brandDesc = getTranslation(brand, 'description') || '';
  const style = brandStyles[slug] || { color: '#F97316', logo: '🏷️' };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={brandName}
        description={brandDesc}
        keywords={`${brandName}, fólie, window film`}
        url={`/brands/${slug}`}
      />

      {/* Hero */}
      <div className="text-white py-16" style={{ background: `linear-gradient(135deg, ${style.color}, ${style.color}dd)` }}>
        <div className="container mx-auto px-4 text-center">
          {brand.logo && (
            <img src={brand.logo} alt={brandName} className="h-16 mx-auto mb-6 object-contain bg-white/20 rounded-lg p-2" />
          )}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{brandName}</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">{brandDesc}</p>
        </div>
      </div>

      {/* Brand info */}
      <div className="container mx-auto px-4 py-12">
        {brand.website && (
          <div className="text-center mb-8">
            <a
              href={brand.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: style.color }}
            >
              {t('brands.visit_website', 'Official website')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}

        {/* Products */}
        {products.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-6 text-center">
              {t('brands.products', 'Products')} {brandName}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map(product => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="bg-white rounded-xl shadow hover:shadow-lg transition-shadow overflow-hidden"
                >
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    {product.images && product.images[0] ? (
                      <img src={product.images[0]} alt={product.name || ''}  className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-4xl">{style.logo}</span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 line-clamp-2">{product.name || ''}  </h3>
                    {product.price && (
                      <p className="mt-2 text-lg font-bold" style={{ color: style.color }}>
                        €{Number(product.price).toFixed(2)}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {products.length === 0 && (
          <div className="text-center py-8">
            <Link
              to={`/catalog?brands=${brand.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: style.color }}
            >
              {t('categories.view_all', 'View all products')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        )}

        {/* Back link */}
        <div className="text-center mt-12">
          <Link to="/brands" className="text-gray-500 hover:text-orange-500 transition-colors">
            ← {t('brands.all_brands', 'All brands')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BrandPage;
